import { HTTPAgentService } from "adapters/http";
import { AGENT_INTERNAL_IDS } from "config/voiceAgent.constants";
import { getTenantFromToken } from "config/helpers/common_helper";

const requireTenant = () => {
  const tenantId = getTenantFromToken();
  if (!tenantId) {
    throw new Error("Tenant not resolved");
  }
  return tenantId;
};

export const listVoices = async (filters = {}) => {
  const tenantId = requireTenant();
  const res = await HTTPAgentService.get(`/agents/tenants/${tenantId}/voices`, {
    params: filters,
  });
  return res.data;
};

export const getAgentConfig = async (agentId = AGENT_INTERNAL_IDS.BARI) => {
  const tenantId = requireTenant();
  const res = await HTTPAgentService.get(
    `/agents/tenants/${tenantId}/${agentId}/config`
  );
  return res.data;
};

export const updateAgentConfig = async (
  payload,
  agentId = AGENT_INTERNAL_IDS.BARI
) => {
  const tenantId = requireTenant();
  const res = await HTTPAgentService.patch(
    `/agents/tenants/${tenantId}/${agentId}/config`,
    payload
  );
  return res.data;
};

export const previewVoice = async (
  payload,
  agentId = AGENT_INTERNAL_IDS.BARI
) => {
  const tenantId = requireTenant();
  const res = await HTTPAgentService.post(
    `/agents/tenants/${tenantId}/${agentId}/preview`,
    payload
  );
  return res.data;
};

/**
 * Streaming sibling of `previewVoice`. Uses fetch() directly (not axios)
 * because we need the response body as a ReadableStream<Uint8Array> for
 * MediaSource feeding, and axios in the browser buffers the full response.
 *
 * Returns { response, abort }:
 *   - response: native Response object — caller reads `response.body`
 *     (ReadableStream) and headers like `X-Rendered-Text`.
 *   - abort:    function that cancels the in-flight request (so the user
 *     clicking another voice mid-stream doesn't leak the previous fetch).
 *
 * Throws on non-2xx after attempting to read a JSON error body (matches
 * the existing previewVoice error contract — 400/429/502/503 propagate
 * up with a `.status` field and a server-supplied `.message`).
 */
export const previewVoiceStream = async (
  payload,
  agentId = AGENT_INTERNAL_IDS.BARI
) => {
  const tenantId = requireTenant();
  const controller = new AbortController();
  const token = localStorage.getItem("jwtToken");

  let response;
  try {
    response = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/agents/tenants/${tenantId}/${agentId}/preview/stream`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      }
    );
  } catch (err) {
    // Network-level failure (DNS, offline, abort, etc.)
    const wrapped = new Error(err?.message || "Network error");
    wrapped.response = { status: 0 };
    wrapped.cause = err;
    throw wrapped;
  }

  if (!response.ok) {
    // Mirror axios error shape so call sites can treat both paths uniformly.
    let body = null;
    try {
      body = await response.json();
    } catch (_e) {
      // server didn't return JSON — leave body null
    }
    const err = new Error(body?.message || `HTTP ${response.status}`);
    err.response = { status: response.status, data: body };
    throw err;
  }

  return { response, abort: () => controller.abort() };
};
